import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { TemplateService } from "src/app/services/template.service";
import { filesize } from "src/app/support/filesize";
import { catchError, switchMap } from "rxjs/operators";
import { from, lastValueFrom, throwError } from "rxjs";
import { reportError } from "src/app/support/error-reporting";
import { PrintFailedError } from "src/app/errors/print-failed.error";
import { FileType, getFileTypeByFileName } from "src/app/support/file";
import { BlobPrinterService } from "src/app/services/print/blob/blob-printer.service";
import { sntLog } from "src/app/common/logger";
import { urlToDataUrl } from "src/app/support/blob";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";

export interface HTMLPrintResult {
  content: string;
}

export type PrintServiceOptions = {
  headerTitle: string | undefined;
};

type HtmlToPdfPayload = {
  header: string;
  content: string;
  footer: string;
  additionalOptions?: {
    landscape?: boolean;
  };
};

const logger = sntLog("PrintService");

@Injectable()
export class PrintService {
  readonly #http = inject(HttpClient);
  readonly #templateService = inject(TemplateService);
  readonly #blobPrinterService = inject(BlobPrinterService);
  readonly #storageBackend = inject(StorageBackend);

  async htmlToPdfNoHeaderFooterAndMargins(
    content: string,
    additionalOptions?: {
      landscape?: boolean;
    }
  ): Promise<Blob> {
    const options = {
      format: "A4",
      displayHeaderFooter: true,
      preferCSSPageSize: false,
      emulateMediaTypeScreen: true,
      printBackground: true,
      margin: {
        top: "10px",
        bottom: "10px",
        left: "10px",
        right: "10px"
      },
      ...(additionalOptions || {})
    };

    const payload = {
      content: replaceRelativeUrlWithAbsolute(content),
      header: "",
      footer: "",
      options: options
    };

    const payloadSizeInBytes = getJsonPayloadSizeInBytes(payload);
    logger.debug(`payload size: ${filesize(payloadSizeInBytes)}`);

    const uploadResult = await this.#storageBackend.uploadV3({
      name: "document.pdf",
      body: new Blob([JSON.stringify(payload)]),
      storageType: "temporary"
    });

    const payloadV2 = {
      name: "document.pdf",
      storageId: uploadResult.storageId,
      options
    };

    const responseBlob: Blob = await lastValueFrom(
      this.#http.put<{ url: string }>(
        environment.snoots.print.endpointV3,
        payloadV2,
        {
          responseType: "json"
        }
      )
    ).then((response) => {
      return fetch(response.url).then((response) => response.blob());
    });

    return responseBlob.slice(0, responseBlob.size, "application/pdf");
  }

  async htmlToPdfMultiple({
    pages
  }: {
    pages: HtmlToPdfPayload[];
  }): Promise<Blob> {
    try {
      const options = {
        format: "A4",
        displayHeaderFooter: true,
        preferCSSPageSize: false,
        emulateMediaTypeScreen: true,
        printBackground: true,
        margin: {
          top: "100px",
          bottom: "80px",
          left: "30px",
          right: "30px"
        }
      };

      const payload: {
        headers: string[];
        footers: string[];
        options: any;
        pages: any;
      } = {
        headers: [],
        footers: [],
        options,
        pages: pages.map((page) => {
          const pagePayload: any = {
            content: replaceRelativeUrlWithAbsolute(page.content),
            header: page.header,
            footer: page.footer
          };

          if (page.additionalOptions) {
            pagePayload.options = {
              ...options,
              ...page.additionalOptions
            };
          }

          return pagePayload;
        })
      };

      payload.pages.forEach((page: any) => {
        const headerIndex = payload.headers.indexOf(page.header);
        if (headerIndex === -1) {
          payload.headers.push(page.header);
          page.header = 0;
        } else {
          page.header = headerIndex;
        }

        const footerIndex = payload.footers.indexOf(page.footer);
        if (footerIndex === -1) {
          payload.footers.push(page.footer);
          page.footer = 0;
        } else {
          page.footer = footerIndex;
        }
      });

      const payloadSizeInBytes = getJsonPayloadSizeInBytes(payload);
      logger.debug(`payload size: ${filesize(payloadSizeInBytes)}`);

      const uploadResult = await this.#storageBackend.uploadV3({
        name: "document.pdf",
        body: new Blob([JSON.stringify(payload)]),
        storageType: "temporary"
      });

      const payloadV2 = {
        name: "document.pdf",
        storageId: uploadResult.storageId,
        options
      };

      const pdfResponseBlob: Blob = await lastValueFrom(
        this.#http
          .put<{ url: string }>(
            environment.snoots.print.endpointV3,
            payloadV2,
            {
              responseType: "json"
            }
          )
          .pipe(
            switchMap((response) => {
              return from(
                fetch(response.url).then((response) => response.blob())
              );
            }),
            catchError((httpError) => {
              reportError(new PrintFailedError(httpError, payloadSizeInBytes));
              return throwError(() => httpError);
            })
          )
      );

      const pdfBlob = pdfResponseBlob!.slice(
        0,
        pdfResponseBlob!.size,
        "application/pdf"
      );
      return pdfBlob;
    } catch (ex) {
      reportError(ex);
      return new Blob([""]);
    }
  }

  async htmlToPdf({
    header,
    content,
    footer,
    additionalOptions
  }: HtmlToPdfPayload & {
    additionalOptions?: {
      displayHeaderFooter?: boolean;
      margin?: {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
      };
    };
  }): Promise<Blob> {
    try {
      const defaultOptions = {
        format: "A4",
        displayHeaderFooter: true,
        preferCSSPageSize: false,
        emulateMediaTypeScreen: true,
        printBackground: true,
        margin: {
          top: "100px",
          bottom: "80px",
          left: "30px",
          right: "30px"
        }
      };

      let options = {
        ...defaultOptions
      };

      if (!!additionalOptions) {
        let { margin } = defaultOptions;
        if (!!additionalOptions?.margin) {
          margin = {
            top: additionalOptions.margin?.top ?? margin.top,
            bottom: additionalOptions.margin?.bottom ?? margin.bottom,
            left: additionalOptions.margin?.left ?? margin.left,
            right: additionalOptions.margin?.right ?? margin.right
          };
        }
        options = {
          ...defaultOptions,
          ...additionalOptions,
          margin
        };
      }

      const payload = {
        content: replaceRelativeUrlWithAbsolute(content),
        header,
        footer,
        options
      };

      const payloadSizeInBytes = getJsonPayloadSizeInBytes(payload);
      logger.debug(`payload size: ${filesize(payloadSizeInBytes)}`);

      const uploadResult = await this.#storageBackend.uploadV3({
        name: "document.pdf",
        body: new Blob([JSON.stringify(payload)]),
        storageType: "temporary"
      });

      const payloadV2 = {
        name: "document.pdf",
        storageId: uploadResult.storageId,
        options
      };

      const responseBlob = await lastValueFrom(
        this.#http
          .put<{ url: string }>(
            environment.snoots.print.endpointV3,
            payloadV2,
            {
              responseType: "json"
            }
          )
          .pipe(
            switchMap((response) => {
              return from(
                fetch(response.url).then((response) => response.blob())
              );
            }),
            catchError((httpError) => {
              reportError(new PrintFailedError(httpError, payloadSizeInBytes));
              return throwError(() => httpError);
            })
          )
      );

      const pdfBlob = responseBlob.slice(
        0,
        responseBlob.size,
        "application/pdf"
      );
      return pdfBlob;
    } catch (ex) {
      reportError(ex);
      return new Blob([""]);
    }
  }

  async toPdf(jsonData: string): Promise<Blob> {
    try {
      const payload = await this.toHTML(jsonData);

      return await this.htmlToPdf({
        content: payload.content,
        header: "",
        footer: ""
      });
    } catch (ex) {
      reportError(ex);
      return new Blob([""]);
    }
  }

  async toHTML(
    jsonData: string,
    options?: { dataUrlAttachments?: string[] }
  ): Promise<HTMLPrintResult> {
    const template = JSON.parse(jsonData);
    const dataUrlAttachments: string[] = options?.dataUrlAttachments ?? [];

    const [bodyHTML] = await Promise.all([
      this.renderBodyToHTML(template, dataUrlAttachments)
    ]);

    return <HTMLPrintResult>{
      content: bodyHTML
    };
  }

  async printFile(downloadUrl: string, fileName: string): Promise<void> {
    let fileType = getFileTypeByFileName(fileName!);
    switch (fileType) {
      case FileType.IMAGE: {
        await this.printImg(downloadUrl);
        break;
      }
      case FileType.PDF:
      default: {
        let fileBlob = (await fetch(downloadUrl)).blob();
        await this.printPdf(fileBlob);
        break;
      }
    }
  }

  async printImg(imageUrl: string): Promise<void> {
    const dataUrl = await urlToDataUrl(imageUrl);

    const blob = new Blob(
      [
        `<html>
          <head>
            <style>
              html, body {
                padding: 0;
                margin: 0;
              }

              img {
                max-width: 100vw;
                max-height: 100vh;
                width: auto;
                height: auto;
                object-fit: contain;
                display: block;
                margin: 0 auto;
              }
            </style>
          </head>
          <body>
            <img src="${dataUrl}"/>
          </body>
        </html>`
      ],
      {
        type: "text/html"
      }
    );

    try {
      await this.#blobPrinterService.print({
        blob,
        name: "pms-image.pdf"
      });
    } catch (ex) {
      logger.debug(ex);
    }
  }

  async printPdf(
    pdfBlobPromise: Promise<Blob>,
    name: string = "pms-print.pdf"
  ): Promise<void> {
    const blobFromPromise = await pdfBlobPromise;

    await this.#blobPrinterService.print({
      blob: new Blob([blobFromPromise], { type: "application/pdf" }),
      name
    });
  }

  private async renderBodyToHTML(
    template: any[],
    dataUrlAttachments: string[] = []
  ): Promise<string> {
    const vm = {
      root: true,
      horizontal: false,
      template: template.filter(
        (x: any) => !["preview-watermark"].includes(x.type)
      ),
      title: "",
      previewWatermark: !!template.find(
        (x: any) => x.type === "preview-watermark"
      ),
      cols: "1"
    };

    const [
      bodyEjs,
      customEjs,
      tabEjs,
      imageEjs,
      checklistEjs,
      checkEjs,
      textareaEjs,
      textEjs,
      selectboxEjs,
      signatureEjs,
      spacerEjs,
      tableEjs,
      titleEjs,
      customerSignatureEjs
    ] = await Promise.all([
      fetch("/assets/ejs-templates/body.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/custom.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/tab.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/image.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/checklist.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/check.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/textarea.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/text.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/selectbox.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/signature.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/spacer.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/table.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/title.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/customer-signature.ejs").then((response) =>
        response.text()
      )
    ]);

    const body = this.#templateService.render(bodyEjs, vm, {
      includer: (originalPath: string, parsedPath: string) => {
        switch (originalPath) {
          case "custom": {
            return {
              template: customEjs
            };
          }
          case "body": {
            return {
              template: bodyEjs
            };
          }
          case "tab": {
            return {
              template: tabEjs
            };
          }
          case "image": {
            return {
              template: imageEjs
            };
          }
          case "checklist": {
            return {
              template: checklistEjs
            };
          }
          case "check": {
            return {
              template: checkEjs
            };
          }
          case "textarea": {
            return {
              template: textareaEjs
            };
          }
          case "text": {
            return {
              template: textEjs
            };
          }
          case "selectbox": {
            return {
              template: selectboxEjs
            };
          }
          case "customer-signature": {
            return {
              template: customerSignatureEjs
            };
          }
          case "signature": {
            return {
              template: signatureEjs
            };
          }
          case "spacer": {
            return {
              template: spacerEjs
            };
          }
          case "table": {
            return {
              template: tableEjs
            };
          }
          case "title": {
            return {
              template: titleEjs
            };
          }
          default: {
            return {
              template: originalPath
            };
          }
        }
      }
    });

    const [soapRootTemplate, tailwindCss] = await Promise.all([
      fetch("/assets/ejs-templates/soap/soap-root.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/tailwind.css").then((response) =>
        response.text()
      )
    ]);

    return this.#templateService.render(soapRootTemplate, {
      bodyHtml: body,
      tailwindCss,
      dataUrlAttachments
    });
  }
}

function replaceRelativeUrlWithAbsolute(html: string): string {
  return html.replace(/\/assets/gi, "https://rocket.snoots.pet/assets");
}

function getJsonPayloadSizeInBytes(payload: unknown): number {
  return new Blob([JSON.stringify(payload)]).size;
}
